import { left, right } from 'fp-ts/Either';

import { httpClientFunction } from 'domain/types';
import Feature, { BusinessInsight } from 'domain/entity/feature';
import Preset from 'domain/entity/preset';
import ReadFeaturesClient from 'use-case/features/read/readFeaturesClient';

export default (httpClient: httpClientFunction, baseUrl: string): ReadFeaturesClient => () =>
    httpClient(`${baseUrl}/dashboards/features`, {})
        .then((response) =>
            response.ok
                ? response
                      .json()
                      .then(asFeatures)
                      .then(right)
                      .catch(() => left({ error: new Error(), status: 500 }))
                : left({ error: new Error(), status: response.status })
        )
        .catch(() => left({ error: new Error(), status: 503 }));

const asFeatures = (featuresJson: FeaturesJsonType) =>
    featuresJson.map(
        (featureItem: FeatureJsonType) =>
            new Feature(
                featureItem.name,
                featureItem.key,
                asPresets(featureItem.presets),
                asAtomisablePresets(featureItem.atomisable_presets ?? []),
                asBusinessInsights(featureItem.business_insights ?? [])
            )
    );

const asPresets = (presetsJson: PresetsJsonType) =>
    presetsJson.map(
        (presetJson: PresetJsonType) =>
            new Preset(presetJson.name, presetJson.key, presetJson.widget_types, presetJson.has_dynamic_context || false)
    );

const asAtomisablePresets = (atomisablePresetsJson: AtomisablePresetsJsonType) =>
    atomisablePresetsJson.map((atomisablePresetJson: AtomisablePresetJsonType) => ({
        name: atomisablePresetJson.name,
        key: atomisablePresetJson.key,
        hasDynamicContext: atomisablePresetJson.has_dynamic_context || false,
    }));

const asBusinessInsights = (businessInsightsJson: BusinessInsightsJsonType): BusinessInsight[] =>
    businessInsightsJson.map((businessInsightJson: BusinessInsightJsonType) => ({
        type: businessInsightJson.type,
        feature: businessInsightJson.feature,
        label: businessInsightJson.label,
    }));

type PresetJsonType = {
    name: string;
    key: string;
    ['widget_types']: Array<string>;
    ['has_dynamic_context']?: boolean;
};

type FeaturesJsonType = Array<FeatureJsonType>;

type FeatureJsonType = {
    name: string;
    key: string;
    presets: PresetsJsonType;
    ['atomisable_presets']?: AtomisablePresetsJsonType;
    ['business_insights']?: BusinessInsightsJsonType;
};

type AtomisablePresetJsonType = {
    name: string;
    key: string;
    ['has_dynamic_context']?: boolean;
};
type AtomisablePresetsJsonType = Array<AtomisablePresetJsonType>;

type PresetsJsonType = Array<PresetJsonType>;

type BusinessInsightJsonType = {
    type: string;
    feature: string;
    label: string;
};

type BusinessInsightsJsonType = Array<BusinessInsightJsonType>;
